import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoIcon from "../../../assets/svg/logoIcon";
import createAxiosInstance from "../../../utils/axiosConfig";
import BackButton from "../../common/backButton";
import ChatHistory from "../../common/chatHistory";
import Footer from "../../common/footer";

const AddCustomerLogo = () => {
	const navigate = useNavigate();
	const [file, setFile] = useState<any>(null);
	const [users, setUsers] = useState<any>([]);
	const [selectedUserId, setSelectedUserId] = useState<any>(null);
	const [errors, setErrors] = useState<any>({});
	const [responseError, setResponseError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

	const userData = JSON.parse(localStorage.getItem("userData") || "{}");

	const handleFileChange = (event: any) => {
		const selectedFile = event.target.files[0];
		if (!selectedFile) return;

		const maxSize = 5 * 1024 * 1024; // 5MB
		const maxWidth = 1920; // Example max width
		const maxHeight = 1080; // Example max height
		const maxResolution = 300; // Example max resolution in DPI

		if (selectedFile.size > maxSize) {
			setErrors({ file: "File size exceeds the maximum limit of 5MB." });
			return;
		}

		const reader = new FileReader();
		reader.onload = (e: any) => {
			const img = new Image();
			img.onload = () => {
				const width = img.width;
				const height = img.height;
				const resolution = selectedFile.size / (width * height); // Simplified resolution check

				if (width > maxWidth || height > maxHeight) {
					setErrors({
						file: `Image dimensions should not exceed ${maxWidth}x${maxHeight} pixels.`,
					});
					return;
				}

				if (resolution > maxResolution) {
					setErrors({
						file: `Image resolution should not exceed ${maxResolution} DPI.`,
					});
					return;
				}

				setFile(selectedFile);
				setErrors({});
			};
			img.src = e.target.result;
		};
		reader.readAsDataURL(selectedFile);
	};

	const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const action = e.target.value;
		if (action) {
			setSelectedUserId(action);
		}
	};


	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const handleUserSelect = (userId: string) => {
		setSelectedUserId(userId);
		setIsDropdownOpen(false);
		// Call your existing handleUserChange function if needed
		const syntheticEvent = {
		  target: { name: "user_id", value: userId }
		} as React.ChangeEvent<HTMLSelectElement>;
		handleUserChange(syntheticEvent);
	};


	useEffect(() => {
		const timer = setTimeout(() => {
		  setDebouncedSearchTerm(searchTerm);
		}, 500); // 500ms delay
	  
		return () => clearTimeout(timer);
	}, [searchTerm]);

	useEffect(() => {
		if (!userData?.is_admin || userData?.is_admin == 0) {
			navigate("/");
		}
		fetchUsers(debouncedSearchTerm);
	}, [debouncedSearchTerm])

	// Add this useEffect to handle clicking outside the dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
		const dropdownElement = document.querySelector(".user-dropdown");
		if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
			setIsDropdownOpen(false);
		}
		};
	
		if (isDropdownOpen) {
		document.addEventListener("mousedown", handleClickOutside);
		}
	
		return () => {
		document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isDropdownOpen]);

	const fetchUsers = async (searchQuery: string) => {
		try {
		  const axiosInstance = createAxiosInstance();
		  // If search query is empty, fetch all users, otherwise filter by email
		  const endpoint = searchQuery ? `/users?email=${searchQuery}` : '/users';
		  const response = await axiosInstance.get(endpoint, {
			params: {
				offset: 0,
				limit: 500, // replace this with proper api for getting all users
			},
		});
		  setUsers(response.data); // Assuming this is how you set users in your component
		} catch (error) {
		  console.error("Error fetching users:", error);
		}
	  };


	const handleUpload = async () => {
		if (!file) {
			setErrors({ file: "Please select a file before submit." });
			return;
		}

		setLoading(true);
		const formData = new FormData();
		formData.append("file", file);
		formData.append("user_id", selectedUserId);

		try {
			const axiosInstance = createAxiosInstance({
				"Content-Type": "multipart/form-data",
			});

			const response = await axiosInstance.post(
				`/upload/logo?user_id=${selectedUserId}`,
				formData
			);
			setResponseError("File uploaded successfully");
			setLoading(false);
			setFile(null);
			setSelectedUserId(null);
			setErrors({});
		} catch (error) {
			console.error("Error uploading file:", error);
			setResponseError("Error uploading file. Try again later.");
		}
	};
	
	return (
		<section className="authentication h-100">
			<div className="row align-items-center justify-content-center min-vh-96 w-100">
				<div className="col-md-10 col-lg-9 col-xl-6">
					<div className="card  mx-auto text-center mt-5">
						<div className="card-body">
							<div className="mb-3">
								<LogoIcon />
							</div>
							<h2 className="my-3 my-md-4"> Upload User Logo </h2>
							<div className="fade-in">
								<div className="form-floating mb-3">
								<div
									className="form-select user-dropdown position-relative"
									onClick={() => setIsDropdownOpen(!isDropdownOpen)}
								>
									<div className="text-start">
									{selectedUserId ? users?.results?.find((user: { id: any; }) => user.id === selectedUserId)?.email : "Select a user"}
									</div>

									{isDropdownOpen && (
									<div
										className="dropdown-menu show w-100 position-absolute"
										style={{ left: 0, fontSize: "1rem", lineHeight: 2,  maxHeight: "300px", // Set a fixed max height
											overflowY: "auto",  // Enable vertical scrolling
											overflowX: "hidden" // Prevent horizontal scrolling
										  }}
										onClick={(e) => e.stopPropagation()}
									>
										<div className="dropdown-item sticky-top bg-white" key="user_search">
										<input
											type="text"
											className="form-control"
											placeholder="Search users..."
											value={searchTerm}
											onChange={handleSearchChange}
											onClick={(e) => e.stopPropagation()}
										/>
										</div>
										{users?.results?.map((user: any) => (
										<div
											key={`user_option_${user.id}`}
											className="dropdown-item"
											onClick={(e) => {
											e.stopPropagation();
											handleUserSelect(user.id);
											}}
										>
											<label className="d-flex align-items-center gap-2">
											{user.email}
											</label>
										</div>
										))}
									</div>
									)}
								</div>
								<label htmlFor="floatingSelect">User</label>
								{errors.user_id && (
									<div className="text-danger small">{errors.user_id}</div>
								)}
								</div>
								<div
									className="form-floating mb-3 fade-in-left"
									style={{
										textAlign: "center",
										justifyContent: "center",
										margin: "auto",
									}}
								>
									<input
										type="file"
										className={`form-control ${
											errors.file ? "is-invalid" : ""
										} my-2`}
										id="floatingInput"
										placeholder="********"
										name="file"
										onChange={handleFileChange}
										accept=".png, .jpg, .jpeg"
										style={{
											display: "inline-block",
											margin: "auto",
											textAlign: "center",
										}}
									/>
									<div className="text-secondary small mt-1 text-start">
                                        Please upload a transparent PNG, JPG, or JPEG file, with a maximum file size of 5MB. <br /> 
                                        Recommended dimensions: up to 1920×1080 pixels.
                                    </div>
									{errors.file && (
										<div className="text-danger small">{errors.file}</div>
									)}
								</div>
								<div className="mb-3">
									<button
										type="submit"
										className="btn btn-primary"
										onClick={handleUpload}
										disabled={loading}
									>
										Submit
									</button>
								</div>
							</div>

							{responseError && <p>{responseError}</p>}
							<div className="mb-3">
								<BackButton />
							</div>
						</div>
					</div>
				</div>
				<Footer />
				<ChatHistory
					onNavigate={(path: string) => {
						navigate(path);
					}}
				/>
			</div>
		</section>
	);
};

export default AddCustomerLogo;
